import { Box, Spinner } from '@chakra-ui/react'
import {
	CardScheme,
	CheckoutErrorType,
	loadCheckoutWebComponents,
	type Component
} from '@checkout.com/checkout-web-components'
import { PaymentErrorStage } from 'commons/components/modal/PaymentErrorStage'
import { PaymentTypes } from 'commons/constants/paymentTypes'
import { useBrowserDetection } from 'commons/hooks/others/useBrowserDetection'
import { initializeWalletPayment, submitWalletPayment } from 'commons/services/api/paymentApi'
import type { WalletBillingAddress } from 'commons/types/paymentTypes'
import { createWalletCheckoutOptions } from 'commons/utils/createWalletCheckoutOptions'
import { normalizeWalletBillingAddress } from 'commons/utils/walletBillingAddress'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type WalletPaymentStageProps = {
	requestPubUuid: string
	paymentType: PaymentTypes
	amount: number
	onClose: () => void
	onProcessingChange?: (processing: boolean) => void
	onPaymentCompleted?: () => void
}

export const WalletPaymentStage = ({
	requestPubUuid,
	paymentType,
	amount,
	onClose,
	onProcessingChange,
	onPaymentCompleted
}: WalletPaymentStageProps) => {
	const { t, i18n } = useTranslation()
	const { isSafari, isChrome } = useBrowserDetection()
	const containerRef = useRef<HTMLDivElement>(null)
	const [errorKey, setErrorKey] = useState<string | null>(null)
	// Keep the session language fixed until an explicit retry so translations
	// cannot restart a payment whose token may already be charging the payer.
	const [attempt, setAttempt] = useState(() => ({ language: i18n.language }))
	const [isLoading, setIsLoading] = useState(true)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const walletType =
		paymentType === PaymentTypes.XPAY_GOOGLE || (paymentType === PaymentTypes.XPAY && !isSafari)
			? PaymentTypes.XPAY_GOOGLE
			: PaymentTypes.XPAY_APPLE

	useEffect(() => {
		let cancelled = false
		let walletComponent: Component | undefined
		let billingAddress: WalletBillingAddress | null = null
		let paymentSubmitted = false
		let autoClickDone = false
		let observer: MutationObserver | undefined
		let timer: ReturnType<typeof setTimeout> | undefined
		let autoClickContainer: HTMLDivElement | null = null
		const stopAutoClick = () => {
			autoClickDone = true
			observer?.disconnect()
			clearTimeout(timer)
			autoClickContainer?.removeEventListener('click', stopAutoClick, true)
		}
		const autoClickWallet = () => {
			if (!isChrome || cancelled || autoClickDone || paymentSubmitted) return
			const selector =
				walletType === PaymentTypes.XPAY_GOOGLE ? '#gpay-button-online-api-id' : 'apple-pay-button'
			const button = autoClickContainer?.querySelector<HTMLElement>(selector)
			if (!button) return
			autoClickDone = true
			observer?.disconnect()
			// Let the mounted SDK button finish rendering before trying to open the sheet.
			timer = setTimeout(() => {
				if (!cancelled && !paymentSubmitted && button.isConnected) button.click()
			}, 100)
		}
		onProcessingChange?.(false)
		setErrorKey(null)
		setIsLoading(true)
		setIsSubmitting(false)

		const showError = (key = 'phone_form.payment_error_generic') => {
			stopAutoClick()
			if (!cancelled) {
				onProcessingChange?.(false)
				setIsSubmitting(false)
				setErrorKey(key)
				setIsLoading(false)
			}
		}

		const initialize = async () => {
			try {
				const configuration = await initializeWalletPayment(requestPubUuid, attempt.language)
				if (cancelled) return
				const onAuthorized = async (_: unknown, data: unknown) => {
					if (cancelled) return { continue: false }
					billingAddress = normalizeWalletBillingAddress(data)
					return { continue: !cancelled }
				}
				const checkout = await loadCheckoutWebComponents({
					...createWalletCheckoutOptions(configuration),
					locale: attempt.language === 'fr' ? 'fr-FR' : 'en-GB',
					componentOptions: {
						applepay: {
							mode: 'tokenize',
							captureBillingAddress: true,
							onAuthorized,
							acceptedCardSchemes:
								amount > 60
									? [CardScheme.Visa, CardScheme.CartesBancaires, CardScheme.Mastercard]
									: [CardScheme.CartesBancaires, CardScheme.Visa, CardScheme.Mastercard]
						},
						googlepay: {
							mode: 'tokenize',
							captureBillingAddress: true,
							onAuthorized
						}
					},
					onTokenized: async (_, result) => {
						if (cancelled || paymentSubmitted) return
						if (result.type !== 'applepay' && result.type !== 'googlepay') {
							showError()
							return
						}
						paymentSubmitted = true
						stopAutoClick()
						setIsSubmitting(true)
						onProcessingChange?.(true)
						setIsLoading(true)
						try {
							const response = await submitWalletPayment(
								requestPubUuid,
								result.data.token,
								billingAddress,
								attempt.language
							)
							if (!cancelled) window.location.assign(response.url)
						} catch {
							showError()
						}
					},
					onPaymentCompleted: () => {
						if (!cancelled) onPaymentCompleted?.()
					},
					onError: (_, error) => {
						const codes =
							error.type === CheckoutErrorType.Request ? error.details.requestErrorCodes || [] : []
						showError(
							codes.includes('not_enough_funds')
								? 'phone_form.payment_error_not_enough_funds'
								: codes.includes('invalid_payment_session_data')
									? 'phone_form.payment_error_data'
									: 'phone_form.payment_error_generic'
						)
					}
				})
				if (cancelled) return
				const componentName = walletType === PaymentTypes.XPAY_GOOGLE ? 'googlepay' : 'applepay'
				walletComponent = checkout.create(componentName)
				if (!(await walletComponent.isAvailable())) {
					throw new Error('Wallet unavailable')
				}
				if (cancelled) return
				const container = containerRef.current
				if (!container) throw new Error('Missing wallet container')
				if (isChrome) {
					autoClickContainer = container
					container.addEventListener('click', stopAutoClick, true)
					observer = new MutationObserver(autoClickWallet)
					observer.observe(container, { childList: true, subtree: true })
				}
				walletComponent.mount(container)
				autoClickWallet()
				setIsLoading(false)
			} catch {
				showError()
			}
		}

		void initialize()
		return () => {
			cancelled = true
			stopAutoClick()
			walletComponent?.unmount()
			onProcessingChange?.(false)
		}
	}, [
		requestPubUuid,
		walletType,
		paymentType,
		isChrome,
		amount,
		onPaymentCompleted,
		onProcessingChange,
		attempt
	])

	return (
		<Box w='full' minH='64px' aria-busy={isLoading}>
			<Box
				ref={containerRef}
				data-wallet-container
				w='full'
				display={isSubmitting || errorKey ? 'none' : undefined}
			/>
			{isLoading && !errorKey && (
				<Box display='flex' justifyContent='center' py={4}>
					<Spinner aria-label={t('phone_form.loading_payment_method')} />
				</Box>
			)}
			{errorKey && (
				<PaymentErrorStage
					error={{
						title: t('phone_form.payment_error_wallet_title', {
							wallet: walletType === PaymentTypes.XPAY_GOOGLE ? 'Google Pay' : 'Apple Pay'
						}),
						message: t(errorKey)
					}}
					onRetry={() => {
						setIsLoading(true)
						setErrorKey(null)
						setAttempt({ language: i18n.language })
					}}
					onChoosePaymentMethod={onClose}
				/>
			)}
		</Box>
	)
}
